import { getApiErrorBody } from "./StringUtils";

export const HAS_PAYMENTS = "HAS_PAYMENTS";
export const STOCK_CONSUMED = "STOCK_CONSUMED";

// An invoice or bill with payments cannot be deleted until each payment is
// deleted on its own (each under its own period lock). The list query already
// carries `payments.items`, so the delete dialog can say so before the request.
export const getPaymentCount = (transaction) =>
  transaction?.payments?.items?.length || 0;

const round2 = (x) => Math.round((Number(x) || 0) * 100) / 100;

const sumPayments = (payments) =>
  (payments || []).reduce((sum, payment) => sum + (payment?.total || 0), 0);

/**
 * What is left on a posted invoice once its payments are taken off, shared by
 * useInvoiceTotals, the reducer and the code that needs the figure outside
 * render, so none of them can round it differently.
 *
 * The backend settles in whole rupiah and marks an invoice paid once its
 * payments reach the total rounded half up: Rp1.115,55 is owed as Rp1.116 and
 * Rp1.114,44 as Rp1.114. The remainder is measured against that same rounded
 * total, so "Sisa Pembayaran" is never Rp0 while the invoice is still Belum
 * Lunas, and the last rupiah can still be paid. round2 first so float dust on
 * a total like 1114.4999999 cannot tip the rounding.
 */
export const getInvoiceBalance = (total, payments = []) => {
  const totalPayment = sumPayments(payments);
  const payable = Math.round(round2(total));
  const isSettled = totalPayment >= payable;
  const balanceDue = Math.max(0, payable - totalPayment);
  return {
    totalPayment,
    balanceDue,
    // Already whole rupiah; kept as its own name for the payment forms, which
    // prefill and cap the amount with it.
    settlementAmount: balanceDue,
    isSettled,
  };
};

/**
 * The Lunas badge for a transaction, by the same rule as getInvoiceBalance.
 *
 * The server's `paid` is only trusted when the row carries no payments to
 * check it against: a cash sale is paid with no payment rows at all, and a
 * list query may leave payments out. Once payments are there, the rule wins,
 * so the badge, the Bayar button and Sisa Pembayaran always agree.
 */
export const isTransactionPaid = (transaction) => {
  const payments = transaction?.payments?.items;
  if (typeof transaction?.total !== "number" || !payments?.length) {
    return Boolean(transaction?.paid);
  }
  return getInvoiceBalance(transaction.total, payments).isSettled;
};

/**
 * The server's own sentence for a 409 HAS_PAYMENTS, or null for any other error.
 *
 * `code` is what we branch on and `message` is what we show, untouched — the
 * same split as PERIOD_CLOSED. This still fires when the dialog thought the row
 * had no payments, e.g. one was added from another device after the list loaded.
 */
export const getHasPaymentsMessage = (error) => {
  const body = getApiErrorBody(error);
  if (body?.code !== HAS_PAYMENTS) return null;
  return typeof body.message === "string" && body.message ? body.message : null;
};

/**
 * The server's own sentence for a 409 STOCK_CONSUMED, or null for any other error.
 *
 * Editing a bill's price or discount is refused once part of the stock it added
 * has been sold, because that cost already went into the sale. Same split as
 * HAS_PAYMENTS: branch on `code`, show `message` untouched.
 */
export const getStockConsumedMessage = (error) => {
  const body = getApiErrorBody(error);
  if (body?.code !== STOCK_CONSUMED) return null;
  return typeof body.message === "string" && body.message ? body.message : null;
};
